import { Suspense, useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useCurrentUser, useLogout, useDoctorOnboardingStatus } from '@api/queries';
import NotificationBell from '@components/layout/NotificationBell';
import RoleSwitcher from '@components/layout/RoleSwitcher';
import { activeRole, ROLE_LABEL } from '@auth/roles';
import { useConfirm } from '@components/ui/ConfirmProvider';
import { useUnsavedGuard } from '@components/ui/UnsavedGuard';
import DoctorDocuments from './Documents/DoctorDocuments';
import styles from './DoctorShell.module.css';

/* Core tabs only. Panchakarma / Reports / Settings (+ the scope-to-confirm
   Accounts / Communicate) get added as their sections land. */
const TABS = [
  { to: '/doctor',              label: 'Dashboard',     icon: 'grid-1x2-fill',    end: true },
  { to: '/doctor/calendar',     label: 'Calendar',      icon: 'calendar-week' },
  { to: '/doctor/consultation', label: 'Consultation',  icon: 'clipboard2-pulse' },
  { to: '/doctor/patients',     label: 'Patients',      icon: 'people' },
  { to: '/doctor/schedule',     label: 'Schedule',      icon: 'clock-history' },
  { to: '/doctor/pk',           label: 'PK Sheet',      icon: 'journal-medical' },
  { to: '/doctor/accounts',     label: 'Accounts',      icon: 'wallet2' },
  { to: '/doctor/reports',      label: 'Reports',       icon: 'bar-chart' },
  { to: '/doctor/education',    label: 'Education',     icon: 'mortarboard' },
  { to: '/doctor/promotion',    label: 'Promotion',     icon: 'megaphone' },
  { to: '/doctor/help',         label: 'Help',          icon: 'question-circle' },
  { to: '/doctor/communication', label: 'Communication', icon: 'chat-dots' },
  { to: '/doctor/documents',    label: 'Documents',     icon: 'file-earmark-text' },
  { to: '/doctor/settings',     label: 'Settings',      icon: 'gear' },
];

function initials(name) {
  if (!name) return 'DR';
  return name.trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase();
}

/* Standing prompt to finish documents. Shows through the whole trial (full
   access is unaffected) until the relevant phase is complete. */
function OnboardingBanner({ status, onOpen }) {
  const b = status?.banner;
  if (!b || b === 'none') return null;
  const urgent = b === 'onboarding' || (status?.daysLeft != null && status.daysLeft <= 2);
  const msg = b === 'onboarding'
    ? 'Complete your onboarding documents to finish setting up your account.'
    : `Submit your final documents before your trial ends${status?.daysLeft != null ? ` · ${status.daysLeft} day(s) left` : ''}.`;
  return (
    <div className={`${styles.banner} ${urgent ? styles.bannerUrgent : ''}`}>
      <span className={styles.bannerText}><i className="bi bi-file-earmark-text" aria-hidden="true" /> {msg}</span>
      <button className={styles.bannerBtn} onClick={onOpen}>Submit documents <i className="bi bi-arrow-right" aria-hidden="true" /></button>
    </div>
  );
}

export default function DoctorShell() {
  const { data: user } = useCurrentUser();
  const { data: ob } = useDoctorOnboardingStatus();
  const logout = useLogout();
  const navigate = useNavigate();

  const locked = !!user?.docsLocked;

  useEffect(() => { document.title = 'Doctor · 1Ayurved'; }, []);

  // Onboarding documents still pending → send them to the documents page first
  // (compulsory after first login). Once onboarding is complete the banner flips
  // to "final" and full access resumes.
  const location = useLocation();

  // Desktop collapse (icon rail + hover flyouts) + mobile hamburger dropdown.
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('1ayurved_docnav_collapsed') === '1'; } catch { return false; }
  });
  const toggleCollapsed = () => setCollapsed((v) => {
    const next = !v;
    try { localStorage.setItem('1ayurved_docnav_collapsed', next ? '1' : '0'); } catch { /* ignore */ }
    return next;
  });
  const [navOpen, setNavOpen] = useState(false);
  const guard = useUnsavedGuard();
  const confirm = useConfirm();
  // Warn before leaving a form (e.g. the consultation) with unsaved edits.
  const guardedNav = async (e, to) => {
    setNavOpen(false);
    if (!guard.isDirty()) return;
    e.preventDefault();
    if (await confirm({ message: 'You have unsaved changes. Leave without saving?', confirmLabel: 'Leave', cancelLabel: 'Stay', danger: true })) {
      guard.setDirty(false);
      navigate(to);
    }
  };
  const activeTab = TABS.find((t) => (t.end ? location.pathname === t.to : location.pathname.startsWith(t.to)));

  useEffect(() => {
    if (ob?.banner === 'onboarding' && location.pathname !== '/doctor/documents') {
      navigate('/doctor/documents', { replace: true });
    }
  }, [ob?.banner, location.pathname, navigate]);

  // Trial lapsed with Final docs still missing → confine to the documents panel.
  if (locked) {
    return (
      <div className={styles.shellLocked}>
        <header className={styles.top}>
          <div className={styles.brand}>
            <Link to="/doctor/documents" className={styles.brandLink} aria-label="1Ayurved">
              <img src="/brand/logo.webp" alt="1Ayurved" className={styles.brandLogo} />
            </Link>
            <span className={styles.roleTag}>Doctor</span>
          </div>
          <div className={styles.topRight} style={{ marginLeft: 'auto' }}>
            <div className={styles.userMeta}>
              <div className={styles.userName}>{user?.name || 'Doctor'}</div>
              <div className={styles.userRole}>Access limited</div>
            </div>
            <button className={styles.signout} onClick={logout}>Sign out</button>
          </div>
        </header>
        <main className={styles.main}>
          <div className={styles.lockNotice}>
            <i className="bi bi-lock" aria-hidden="true" /> Your 1-month trial ended with required documents still pending. Submit them below to restore full access.
          </div>
          <DoctorDocuments />
        </main>
      </div>
    );
  }

  return (
    <div className={styles.shell}>
      <header className={styles.topbar}>
        <div className={styles.brand}>
          <Link to="/doctor" className={styles.brandLink} aria-label="1Ayurved — Dashboard">
            <img src="/brand/logo.webp" alt="1Ayurved" className={styles.brandLogo} />
          </Link>
          <span className={styles.roleTag}>Doctor</span>
        </div>

        <div className={styles.topRight}>
          <NotificationBell seeAllPath="/doctor/notifications" />
          <div className={styles.user}>
            <div className={styles.userMeta}>
              <div className={styles.userName}>{user?.name || 'Doctor'}</div>
              <div className={styles.userRole}>{ROLE_LABEL[activeRole(user)] || user?.role || 'doctor'}</div>
            </div>
            <div className={styles.avatar} aria-hidden="true">{initials(user?.name)}</div>
            <button className={styles.logout} onClick={logout} type="button" title="Sign out">
              <i className="bi bi-box-arrow-right" aria-hidden="true" />
              <span className={styles.logoutText}>Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <div className={styles.body}>
        <button
          type="button"
          className={styles.navToggle}
          onClick={() => setNavOpen((o) => !o)}
          aria-expanded={navOpen}
          aria-label="Menu"
        >
          <i className="bi bi-list" aria-hidden="true" />
          <span>{activeTab?.label || 'Menu'}</span>
          <i className={`bi bi-chevron-${navOpen ? 'up' : 'down'}`} aria-hidden="true" />
        </button>

        <aside className={`${styles.side} ${collapsed ? styles.collapsed : ''} ${navOpen ? styles.sideOpen : ''}`}>
          <div className={styles.sideHead}>
            <span className={styles.sideTitle}>Menu</span>
            <button
              type="button"
              className={styles.collapseBtn}
              onClick={toggleCollapsed}
              aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
              title={collapsed ? 'Expand menu' : 'Collapse menu'}
            >
              <i className={`bi ${collapsed ? 'bi-chevron-right' : 'bi-chevron-left'}`} aria-hidden="true" />
            </button>
          </div>
          <div className={styles.roleSwitch}><RoleSwitcher /></div>
          <nav className={styles.nav} aria-label="Doctor sections">
            {TABS.map((t) => (
              <NavLink
                key={t.to}
                to={t.to}
                end={t.end}
                title={t.label}
                onClick={(e) => guardedNav(e, t.to)}
                className={({ isActive }) =>
                  `${styles.item} ${isActive ? styles.itemActive : ''}`
                }
              >
                <i className={`bi bi-${t.icon}`} aria-hidden="true" /> <span className={styles.itemText}>{t.label}</span>
              </NavLink>
            ))}
          </nav>
        </aside>

        <main className={styles.main}>
          <OnboardingBanner status={ob} onOpen={() => navigate('/doctor/documents')} />
          <Suspense fallback={<div className={styles.loading}>Loading…</div>}>
            <Outlet />
          </Suspense>
        </main>
      </div>
    </div>
  );
}
